import { useState, useEffect } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { adminAPI, ticketAPI } from "../services/api";
import {
  Users,
  Ticket,
  TrendingUp,
  Star,
  Plus,
  Search,
  Settings,
  Edit,
  Trash2,
  BarChart3,
  X,
  Save,
  Mail,
  Clock,
  UserCog,
  Loader2,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

export default function AdminDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchQueryTech, setSearchQueryTech] = useState("");
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<any>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    role: "STUDENT" as "ADMIN" | "TECHNICIAN" | "STUDENT",
    departmentId: "",
    phoneNumber: "",
  });
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<any[]>([]);
  const [technicians, setTechnicians] = useState<any[]>([]);
  const [reportsData, setReportsData] = useState<any>(null);
  const [stats, setStats] = useState([
    {
      label: "Total Users",
      value: "0",
      change: "+0%",
      icon: Users,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
    },
    {
      label: "Active Tickets",
      value: "0",
      change: "+0 today",
      icon: Ticket,
      color: "text-yellow-500",
      bgColor: "bg-yellow-500/10",
    },
    {
      label: "Resolution Rate",
      value: "0%",
      change: "+0%",
      icon: TrendingUp,
      color: "text-green-500",
      bgColor: "bg-green-500/10",
    },
    {
      label: "Satisfaction",
      value: "0/5",
      change: "0% positive",
      icon: Star,
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
    },
  ]);
  const [ticketTrends, setTicketTrends] = useState<any[]>([]);
  const [avgResolutionTime, setAvgResolutionTime] = useState<string>("N/A");
  const [ticketsList, setTicketsList] = useState<any[]>([]);

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    try {
      setLoading(true);

      const [
        usersResponse,
        ticketsResponse,
        reportsResponse,
        techniciansResponse,
      ] = await Promise.all([
        adminAPI.getUsers(),
        ticketAPI.list(),
        adminAPI.reports({}),
        adminAPI.technicianStats(),
      ]);

      if (usersResponse.success || Array.isArray(usersResponse)) {
        const usersList = Array.isArray(usersResponse)
          ? usersResponse
          : usersResponse.data?.users ||
            usersResponse.data?.items ||
            usersResponse.data ||
            [];
        setUsers(Array.isArray(usersList) ? usersList : []);

        const usersCount = Array.isArray(usersList) ? usersList.length : 0;
        setStats((prev) => [
          { ...prev[0], value: String(usersCount) },
          prev[1],
          prev[2],
          prev[3],
        ]);
      } else {
        setUsers([]);
      }

      if (techniciansResponse.success && techniciansResponse.data) {
        const techList = Array.isArray(techniciansResponse.data)
          ? techniciansResponse.data
          : [];

        setTechnicians(techList);
      } else {
        setTechnicians([]);
      }

      if (ticketsResponse.success && ticketsResponse.data) {
        const ticketsList = Array.isArray(ticketsResponse.data.data)
          ? ticketsResponse.data.data
          : Array.isArray(ticketsResponse.data.tickets)
            ? ticketsResponse.data.tickets
            : Array.isArray(ticketsResponse.data)
              ? ticketsResponse.data
              : [];

        setTicketsList(ticketsList);

        const activeCount = ticketsList.filter(
          (t: any) => t.status !== "CLOSED" && t.status !== "RESOLVED",
        ).length;

        const resolvedCount = ticketsList.filter(
          (t: any) => t.status === "RESOLVED" || t.status === "CLOSED",
        ).length;

        const resolutionRate =
          ticketsList.length > 0
            ? ((resolvedCount / ticketsList.length) * 100).toFixed(1)
            : "0";

        const categoryMap: any = {};
        ticketsList.forEach((ticket: any) => {
          const cat = ticket.category?.name || "Other";
          categoryMap[cat] = (categoryMap[cat] || 0) + 1;
        });

        const trends = Object.entries(categoryMap)
          .map(([category, count]) => ({
            category,
            count: count as number,
            percentage:
              ticketsList.length > 0
                ? Math.round(((count as number) / ticketsList.length) * 100)
                : 0,
          }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 4);

        setTicketTrends(trends);

        const resolvedTickets = ticketsList.filter((t: any) => {
          const isResolved = t.status === "RESOLVED" || t.status === "CLOSED";
          const hasResolveDate = t.resolvedAt || t.updatedAt;
          return isResolved && hasResolveDate && t.createdAt;
        });
        let calculatedAvgResolutionTime = "N/A";
        if (resolvedTickets.length > 0) {
          const totalTime = resolvedTickets.reduce(
            (sum: number, ticket: any) => {
              const created = new Date(ticket.createdAt).getTime();
              const resolveDate = ticket.resolvedAt || ticket.updatedAt;
              const resolved = new Date(resolveDate).getTime();
              return sum + (resolved - created);
            },
            0,
          );
          const avgMs = totalTime / resolvedTickets.length;
          const avgHours = Math.round(avgMs / (1000 * 60 * 60));
          calculatedAvgResolutionTime = `${avgHours}h`;
        }
        setAvgResolutionTime(calculatedAvgResolutionTime);

        setStats((prev) => [
          prev[0],
          {
            ...prev[1],
            value: String(activeCount),
            change: `${ticketsList.length} total`,
          },
          {
            ...prev[2],
            value: `${resolutionRate}%`,
            change: `${resolvedCount} resolved`,
          },
          prev[3],
        ]);
      }

      if (reportsResponse.success && reportsResponse.data) {
        const reportData = reportsResponse.data;
        setReportsData(reportData);

        const avgRating = reportData.feedback?.averageRating || 0;
        const totalFeedback = reportData.feedback?.totalResponses || 0;

        setStats((prev) => [
          prev[0],
          prev[1],
          prev[2],
          {
            ...prev[3],
            value: avgRating > 0 ? `${avgRating.toFixed(1)}/5` : "N/A",
            change: `${totalFeedback} reviews`,
          },
        ]);
      }
    } catch (error) {
      toast.error("Failed to load admin dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const calculateTechnicianAvgResolutionTime = (
    technicianId: string,
  ): string => {
    const techResolvedTickets = ticketsList.filter((t: any) => {
      const isResolved = t.status === "RESOLVED" || t.status === "CLOSED";
      const isAssignedToTech =
        t.assignee?.id === technicianId || t.assignedId === technicianId;
      const hasResolveDate = t.resolvedAt || t.updatedAt || t.dateResolved;

      return isResolved && isAssignedToTech && hasResolveDate && t.createdAt;
    });

    if (techResolvedTickets.length === 0) return "N/A";

    const totalTime = techResolvedTickets.reduce((sum: number, ticket: any) => {
      const created = new Date(ticket.createdAt).getTime();
      const resolveDate =
        ticket.resolvedAt || ticket.dateResolved || ticket.updatedAt;
      const resolved = new Date(resolveDate).getTime();
      return sum + (resolved - created);
    }, 0);

    const avgMs = totalTime / techResolvedTickets.length;
    const avgHours = Math.round(avgMs / (1000 * 60 * 60));
    return `${avgHours}h`;
  };

  const openEditDialog = (user: any) => {
    setSelectedUser(user);
    setFormData({
      fullName: user.fullName || "",
      email: user.email || "",
      password: "",
      role: user.role || "STUDENT",
      departmentId: user.departmentId || "",
      phoneNumber: user.phoneNumber || "",
    });
    setShowUserDialog(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (selectedUser) {
        const response = await adminAPI.updateUser(selectedUser.id, {
          fullName: formData.fullName,
          email: formData.email,
          role: formData.role,
          departmentId: formData.departmentId || null,
          phoneNumber: formData.phoneNumber || null,
          ...(formData.password && { password: formData.password }),
        });

        if (response.success) {
          toast.success("User updated successfully");
          loadAdminData();
          setShowUserDialog(false);
        }
      } else {
        if (!formData.password) {
          toast.error("Password is required for new users");
          return;
        }

        const response = await adminAPI.createUser({
          fullName: formData.fullName,
          email: formData.email,
          password: formData.password,
          role: formData.role,
          departmentId: formData.departmentId || null,
          phoneNumber: formData.phoneNumber || null,
          organizationId: user?.organizationId,
        });

        if (response.success) {
          toast.success("User created successfully");
          loadAdminData();
          setShowUserDialog(false);
        }
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to save user");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (userId: string, userName: string) => {
    setUserToDelete({ id: userId, name: userName });
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);
    try {
      await adminAPI.deleteUser(userToDelete.id);
      toast.success("User deleted successfully");
      loadAdminData();
      setShowDeleteDialog(false);
      setUserToDelete(null);
    } catch (error) {
      toast.error("Failed to delete user");
    } finally {
      setIsDeleting(false);
    }
  };

  const openCreateDialog = () => {
    setSelectedUser(null);
    setFormData({
      fullName: "",
      email: "",
      password: "",
      role: "STUDENT",
      departmentId: "",
      phoneNumber: "",
    });
    setShowUserDialog(true);
  };

  const roleColors = {
    admin: "bg-red-500/10 text-red-400",
    technician: "bg-blue-500/10 text-blue-400",
    student: "bg-green-500/10 text-green-400",
  };

  return (
    <DashboardLayout>
      <div className="min-h-full p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2 font-serif">
            Administration Panel
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            System management, user administration, and analytics
          </p>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-card border border-border/50 rounded-xl p-4 sm:p-6 hover:border-primary/30 transition-all shadow-lg"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div
                      className={`${stat.bgColor} ${stat.color} p-3 rounded-lg`}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                  </div>
                  <div className="text-2xl sm:text-3xl font-bold text-foreground mb-1 font-mono">
                    {stat.value}
                  </div>
                  <div className="text-sm text-muted-foreground mb-1">
                    {stat.label}
                  </div>
                  <div className="text-xs text-primary">{stat.change}</div>
                </motion.div>
              );
            })}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 space-y-6">
            <div className="bg-card border border-border/50 rounded-xl shadow-lg overflow-hidden">
              <div className="border-b border-border/50 p-4 sm:p-6">
                <div className="flex flex-wrap gap-2">
                  {["overview", "users", "technicians"].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-all cursor-pointer ${
                        activeTab === tab
                          ? "bg-primary text-white"
                          : "bg-muted/50 text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {activeTab === "users" && (
                <div>
                  <div className="p-4 sm:p-6 border-b border-border/50">
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <input
                          type="search"
                          placeholder="Search users..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground text-sm"
                        />
                      </div>
                      <button
                        onClick={openCreateDialog}
                        className="flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg transition-all shadow-lg whitespace-nowrap cursor-pointer"
                      >
                        <Plus className="w-4 h-4" />
                        <span className="hidden sm:inline">Add User</span>
                      </button>
                    </div>
                  </div>

                  <div className="divide-y divide-border/50">
                    {(() => {
                      const filteredUsers = Array.isArray(users)
                        ? users.filter((user) => {
                            const matchesSearch =
                              (user.fullName || "")
                                .toLowerCase()
                                .includes(searchQuery.toLowerCase()) ||
                              (user.email || "")
                                .toLowerCase()
                                .includes(searchQuery.toLowerCase());
                            return matchesSearch;
                          })
                        : [];

                      if (filteredUsers.length === 0) {
                        return (
                          <div className="text-center py-12 text-muted-foreground">
                            <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>
                              {searchQuery
                                ? "No users found matching your search"
                                : "No users found"}
                            </p>
                          </div>
                        );
                      }

                      return filteredUsers.map((user) => (
                        <div
                          key={user.id}
                          className="p-4 sm:p-6 hover:bg-muted/30 transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-sm font-bold text-primary">
                                {user.fullName
                                  ? user.fullName
                                      .split(" ")
                                      .map((n: string) => n[0])
                                      .join("")
                                  : "??"}
                              </span>
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="text-sm font-semibold text-foreground truncate">
                                  {user.fullName || "Unknown User"}
                                </h3>
                                <span
                                  className={`px-2 py-0.5 text-xs font-medium rounded ${
                                    roleColors[
                                      user.role as keyof typeof roleColors
                                    ] || "bg-gray-500/20 text-gray-300"
                                  }`}
                                >
                                  {user.role || "STUDENT"}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground truncate">
                                {user.email || "No email"}
                              </p>
                              <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                                <span>
                                  ID:{" "}
                                  {user.id ? user.id.substring(0, 8) : "N/A"}...
                                </span>
                                {user.createdAt && (
                                  <>
                                    <span>•</span>
                                    <span>
                                      Joined{" "}
                                      {new Date(
                                        user.createdAt,
                                      ).toLocaleDateString()}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openEditDialog(user)}
                                className="p-2 hover:bg-muted rounded-lg transition-colors cursor-pointer"
                              >
                                <Edit className="w-4 h-4 text-muted-foreground" />
                              </button>
                              <button
                                onClick={() =>
                                  handleDelete(
                                    user.id,
                                    user.fullName || "this user",
                                  )
                                }
                                className="p-2 hover:bg-red-500/10 rounded-lg transition-colors cursor-pointer"
                              >
                                <Trash2 className="w-4 h-4 text-red-400" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}

              {activeTab === "technicians" && (
                <div className="p-4 sm:p-6">
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <h3 className="text-lg font-bold text-foreground font-serif">
                        Technician Performance & Management
                      </h3>
                      <div className="relative w-full sm:w-80">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <input
                          type="search"
                          placeholder="Search technicians..."
                          value={searchQueryTech}
                          onChange={(e) => setSearchQueryTech(e.target.value)}
                          className="w-full pl-10 pr-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground text-sm"
                        />
                      </div>
                    </div>

                    {(() => {
                      const filteredTechnicians = Array.isArray(technicians)
                        ? technicians.filter((tech) => {
                            const techData = tech.technician || tech;
                            const name = techData.fullName || "";
                            const email = techData.email || "";
                            const matchesSearch =
                              name
                                .toLowerCase()
                                .includes(searchQueryTech.toLowerCase()) ||
                              email
                                .toLowerCase()
                                .includes(searchQueryTech.toLowerCase());
                            return matchesSearch;
                          })
                        : [];

                      if (filteredTechnicians.length === 0) {
                        return (
                          <div className="text-center py-12 text-muted-foreground">
                            <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>
                              {searchQueryTech
                                ? "No technicians found matching your search"
                                : "No technicians found"}
                            </p>
                          </div>
                        );
                      }

                      return (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-border/50">
                                <th className="text-left p-3 text-sm font-semibold text-foreground">
                                  Technician
                                </th>
                                <th className="text-left p-3 text-sm font-semibold text-foreground">
                                  Assigned
                                </th>
                                <th className="text-left p-3 text-sm font-semibold text-foreground">
                                  Resolved
                                </th>
                                <th className="text-left p-3 text-sm font-semibold text-foreground">
                                  Rate
                                </th>
                                <th className="text-left p-3 text-sm font-semibold text-foreground">
                                  Avg Time
                                </th>
                                <th className="text-left p-3 text-sm font-semibold text-foreground">
                                  Rating
                                </th>
                                <th className="text-right p-3 text-sm font-semibold text-foreground">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredTechnicians.map((tech) => {
                                const techData = tech.technician || tech;
                                const resolvedCount = tech.resolvedTickets || 0;
                                const assignedCount =
                                  tech.activeTickets || tech.totalTickets || 0;
                                const rate =
                                  assignedCount > 0
                                    ? (
                                        (resolvedCount / assignedCount) *
                                        100
                                      ).toFixed(1)
                                    : "0";
                                const avgTime =
                                  calculateTechnicianAvgResolutionTime(
                                    techData.id || tech.id,
                                  );
                                const rating = tech.averageRating || "N/A";

                                return (
                                  <tr
                                    key={techData.id || tech.id}
                                    className="border-b border-border/30 hover:bg-muted/20 transition-colors"
                                  >
                                    <td className="p-3">
                                      <div>
                                        <p className="text-sm font-medium text-foreground">
                                          {techData.fullName || "Unknown"}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                          {techData.email}
                                        </p>
                                      </div>
                                    </td>
                                    <td className="p-3">
                                      <span className="text-sm text-foreground">
                                        {assignedCount}
                                      </span>
                                    </td>
                                    <td className="p-3">
                                      <span className="text-sm font-medium text-green-400">
                                        {resolvedCount}
                                      </span>
                                    </td>
                                    <td className="p-3">
                                      <span
                                        className={`text-sm font-semibold ${
                                          parseFloat(rate) >= 93
                                            ? "text-green-400"
                                            : parseFloat(rate) >= 90
                                              ? "text-blue-400"
                                              : "text-yellow-400"
                                        }`}
                                      >
                                        {rate}%
                                      </span>
                                    </td>
                                    <td className="p-3">
                                      <span className="text-sm text-muted-foreground">
                                        {avgTime}
                                      </span>
                                    </td>
                                    <td className="p-3">
                                      <div className="flex items-center gap-1">
                                        <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                                        <span className="text-sm font-medium text-foreground">
                                          {rating}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="p-3">
                                      <div className="flex items-center justify-end gap-2">
                                        <button
                                          onClick={() => {
                                            toast.info("Reassign Tickets", {
                                              description: `Managing ${tech.fullName}'s ticket assignments`,
                                            });
                                          }}
                                          className="p-1.5 hover:bg-muted rounded-lg transition-colors cursor-pointer"
                                          title="Reassign Tickets"
                                        >
                                          <UserCog className="w-4 h-4 text-primary" />
                                        </button>
                                        <button
                                          onClick={() => openEditDialog(tech)}
                                          className="p-1.5 hover:bg-muted rounded-lg transition-colors cursor-pointer"
                                          title="Edit"
                                        >
                                          <Edit className="w-4 h-4 text-muted-foreground" />
                                        </button>
                                        <button
                                          onClick={() =>
                                            handleDelete(
                                              tech.id,
                                              tech.fullName ||
                                                "this technician",
                                            )
                                          }
                                          className="p-1.5 hover:bg-muted rounded-lg transition-colors cursor-pointer"
                                          title="Delete"
                                        >
                                          <Trash2 className="w-4 h-4 text-red-400" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                      <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="w-4 h-4 text-green-400" />
                          <p className="text-xs font-medium text-green-400">
                            Overall Performance
                          </p>
                        </div>
                        <p className="text-2xl font-bold text-foreground">
                          {technicians.length > 0
                            ? (
                                technicians.reduce((sum, t) => {
                                  const resolved = t.resolvedTickets || 0;
                                  const assigned = t.assignedTickets || 1;
                                  return sum + (resolved / assigned) * 100;
                                }, 0) / technicians.length
                              ).toFixed(1)
                            : "0"}
                          %
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Average Resolution Rate
                        </p>
                      </div>

                      <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="w-4 h-4 text-blue-400" />
                          <p className="text-xs font-medium text-blue-400">
                            Response Time
                          </p>
                        </div>
                        <p className="text-2xl font-bold text-foreground">
                          {avgResolutionTime}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Team Average Resolution
                        </p>
                      </div>

                      <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Star className="w-4 h-4 text-purple-400" />
                          <p className="text-xs font-medium text-purple-400">
                            Satisfaction
                          </p>
                        </div>
                        <p className="text-2xl font-bold text-foreground">
                          {reportsData?.feedback?.averageRating?.toFixed(1) ||
                            "N/A"}
                          /5
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Average Customer Rating
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "overview" && (
                <div className="p-4 sm:p-6">
                  <h3 className="text-lg font-bold text-foreground mb-4 font-serif">
                    System Overview
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <p className="text-2xl font-bold text-foreground mb-1">
                        {reportsData?.totalTickets || 0}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Total Tickets
                      </p>
                    </div>
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <p className="text-2xl font-bold text-foreground mb-1">
                        {avgResolutionTime}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Avg Resolution
                      </p>
                    </div>
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <p className="text-2xl font-bold text-foreground mb-1">
                        {technicians.length}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Active Technicians
                      </p>
                    </div>
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <p className="text-2xl font-bold text-foreground mb-1">
                        {reportsData?.totalTickets && reportsData?.resolvedCount
                          ? `${Math.round((reportsData.resolvedCount / reportsData.totalTickets) * 100)}%`
                          : "N/A"}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Resolution Rate
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-card border border-border/50 rounded-xl p-4 sm:p-6 shadow-lg">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-primary" />
                <h3 className="text-sm font-bold text-foreground">
                  Ticket Distribution
                </h3>
              </div>
              <div className="space-y-3">
                {ticketTrends.map((trend, idx) => (
                  <div key={idx}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="text-foreground font-medium">
                        {trend.category}
                      </span>
                      <span className="text-muted-foreground">
                        {trend.count}
                      </span>
                    </div>
                    <div className="h-2 bg-muted/50 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full"
                        style={{ width: `${trend.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-card border border-border/50 rounded-xl p-4 sm:p-6 shadow-lg">
              <div className="flex items-center gap-2 mb-4">
                <Settings className="w-5 h-5 text-primary" />
                <h3 className="text-sm font-bold text-foreground">
                  Quick Actions
                </h3>
              </div>
              <div className="space-y-2">
                {[
                  {
                    icon: Users,
                    label: "User Roles",
                    action: () => {
                      setActiveTab("users");
                      toast.info("User Management", {
                        description: "Manage user roles and permissions",
                      });
                    },
                  },
                  {
                    icon: BarChart3,
                    label: "View Analytics",
                    action: () => {
                      window.location.href = "/dashboard/reports";
                    },
                  },
                ].map((action, idx) => {
                  const Icon = action.icon;
                  return (
                    <button
                      key={idx}
                      onClick={action.action}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors text-left cursor-pointer"
                    >
                      <Icon className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium text-foreground">
                        {action.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showConfigDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-bold text-foreground font-serif">
                  System Configuration
                </h2>
              </div>
              <button
                onClick={() => setShowConfigDialog(false)}
                className="p-2 hover:bg-muted rounded-lg transition-colors cursor-pointer"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <UserCog className="w-4 h-4 text-primary" />
                  Ticket Routing Rules
                </h3>
                <div className="space-y-2">
                  {[
                    {
                      category: "Hardware",
                      assignTo: "Mike Chen",
                      priority: "High",
                    },
                    {
                      category: "Software",
                      assignTo: "David Kim",
                      priority: "Medium",
                    },
                    {
                      category: "Network",
                      assignTo: "Sarah Johnson",
                      priority: "Critical",
                    },
                    {
                      category: "Critical Priority",
                      assignTo: "Alex Rivera",
                      priority: "Critical",
                    },
                  ].map((rule, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border/30"
                    >
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {rule.category}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Auto-assign to {rule.assignTo}
                        </p>
                      </div>
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded ${
                          rule.priority === "Critical"
                            ? "bg-red-500/20 text-red-400"
                            : rule.priority === "High"
                              ? "bg-orange-500/20 text-orange-400"
                              : "bg-blue-500/20 text-blue-400"
                        }`}
                      >
                        {rule.priority}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary" />
                  SLA Time Limits
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    {
                      priority: "Critical",
                      response: "15 min",
                      resolve: "2 hours",
                    },
                    {
                      priority: "High",
                      response: "30 min",
                      resolve: "4 hours",
                    },
                    {
                      priority: "Medium",
                      response: "2 hours",
                      resolve: "24 hours",
                    },
                    {
                      priority: "Low",
                      response: "4 hours",
                      resolve: "48 hours",
                    },
                  ].map((sla, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-muted/30 rounded-lg border border-border/30"
                    >
                      <p className="text-sm font-medium text-foreground mb-2">
                        {sla.priority} Priority
                      </p>
                      <div className="space-y-1 text-xs text-muted-foreground">
                        <p>Response: {sla.response}</p>
                        <p>Resolution: {sla.resolve}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary" />
                  Email Notifications
                </h3>
                <div className="space-y-2">
                  {[
                    { event: "Ticket Created", enabled: true },
                    { event: "Ticket Assigned", enabled: true },
                    { event: "Status Changed", enabled: true },
                    { event: "Ticket Resolved", enabled: true },
                    { event: "Feedback Requested", enabled: true },
                    { event: "Escalation", enabled: true },
                  ].map((notification, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border/30"
                    >
                      <span className="text-sm text-foreground">
                        {notification.event}
                      </span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          defaultChecked={notification.enabled}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-primary" />
                  Performance Thresholds
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="p-3 bg-muted/30 rounded-lg border border-border/30">
                    <p className="text-sm text-muted-foreground mb-1">
                      Min Resolution Rate
                    </p>
                    <p className="text-xl font-bold text-foreground">85%</p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg border border-border/30">
                    <p className="text-sm text-muted-foreground mb-1">
                      Max Response Time
                    </p>
                    <p className="text-xl font-bold text-foreground">30 min</p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg border border-border/30">
                    <p className="text-sm text-muted-foreground mb-1">
                      Min Satisfaction
                    </p>
                    <p className="text-xl font-bold text-foreground">4.5/5</p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg border border-border/30">
                    <p className="text-sm text-muted-foreground mb-1">
                      Weekly Ticket Target
                    </p>
                    <p className="text-xl font-bold text-foreground">20</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-card border-t border-border p-4 flex justify-end gap-2">
              <button
                onClick={() => setShowConfigDialog(false)}
                className="px-4 py-2 rounded-lg hover:bg-muted transition-colors text-sm font-medium text-muted-foreground cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  toast.success("Settings Saved", {
                    description: "System configuration updated successfully",
                  });
                  setShowConfigDialog(false);
                }}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors text-sm font-medium flex items-center gap-2 cursor-pointer"
              >
                <Save className="w-4 h-4" />
                Save Changes
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {showUserDialog && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border/50 rounded-2xl p-6 max-w-lg w-full shadow-2xl"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-foreground">
                {selectedUser ? "Edit User" : "Create New User"}
              </h3>
              <button
                onClick={() => {
                  setShowUserDialog(false);
                  setSelectedUser(null);
                }}
                className="p-2 hover:bg-muted rounded-lg transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) =>
                    setFormData({ ...formData, fullName: e.target.value })
                  }
                  required
                  className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                  className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Password {!selectedUser && "*"}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  required={!selectedUser}
                  placeholder={
                    selectedUser ? "Leave blank to keep current" : ""
                  }
                  className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Role *
                </label>
                <select
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      role: e.target.value as
                        | "ADMIN"
                        | "TECHNICIAN"
                        | "STUDENT",
                    })
                  }
                  required
                  className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground"
                >
                  <option value="STUDENT">Student</option>
                  <option value="TECHNICIAN">Technician</option>
                  <option value="ADMIN">Administrator</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) =>
                    setFormData({ ...formData, phoneNumber: e.target.value })
                  }
                  className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowUserDialog(false);
                    setSelectedUser(null);
                  }}
                  className="flex-1 px-4 py-2.5 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-all font-medium cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-all disabled:opacity-50 cursor-pointer"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      {selectedUser ? "Update" : "Create"} User
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {showDeleteDialog && userToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border/50 rounded-2xl p-6 max-w-md w-full shadow-2xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-500/10 rounded-full">
                <Trash2 className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-foreground">
                  Delete User
                </h3>
                <p className="text-sm text-muted-foreground">
                  This action cannot be undone
                </p>
              </div>
            </div>

            <p className="text-foreground mb-6">
              Are you sure you want to delete{" "}
              <span className="font-semibold text-red-400">
                {userToDelete.name}
              </span>
              ? All their data will be permanently removed from the system.
            </p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteDialog(false);
                  setUserToDelete(null);
                }}
                disabled={isDeleting}
                className="flex-1 px-4 py-2.5 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-all font-medium cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={isDeleting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all font-medium cursor-pointer disabled:opacity-50"
              >
                {isDeleting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Trash2 className="w-5 h-5" />
                    Delete User
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </DashboardLayout>
  );
}
